import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { ClientProvider, useClient } from './ClientContext';
import Icon from './components/Icon';
import Login from './Login';
import Overview from './pages/Overview';
import ApprovalQueue from './pages/ApprovalQueue';
import AuditLog from './pages/AuditLog';
import RulesConfig from './pages/RulesConfig';
import CreativeLibrary from './pages/CreativeLibrary';
import BrandGuidelines from './pages/BrandGuidelines';
import AgenticStudio from './pages/AgenticStudio';
import FeedStudio from './pages/feedstudio/FeedStudio';
import CampaignBuild from './pages/CampaignBuild';
import BulkCampaigns from './pages/BulkCampaigns';
import Settings from './pages/Settings';
import ReportingDashboard from './pages/reporting/Dashboard';
import AIInsights from './pages/reporting/AIInsights';
import OptiBid from './pages/reporting/OptiBid';
import './index.css';

/**
 * The glide rail.
 *
 * The sidebar is a 68px icon rail that unfurls to 248px when the pointer
 * enters it — as an overlay, so the page never reflows. Three motions, each
 * of which explains something:
 *
 *   The rail unfurling says "you are navigating now".
 *   The brass indicator travelling between items says "you came from there".
 *   The rail closing after a click says "done — back to the work".
 *
 * That last one is the behaviour to protect: clicking a destination collapses
 * the rail immediately (the `resting` state), even though the pointer is
 * still inside it. It reopens on the next fresh entry. Without this, the rail
 * sits open over the page you just asked for.
 *
 * The old collapsible groups (AI Buying / Creative / AI Reporting) became
 * always-visible section labels. A rail that closes itself cannot also ask
 * you to expand a group first — every destination must be one click.
 */

const NAV = [
  { section: null, items: [
    { to: '/', end: true, icon: 'cards', label: 'Overview' },
  ]},
  { section: 'AI Buying', items: [
    { to: '/build',     icon: 'spark',  label: 'Campaign Build' },
    { to: '/bulk',      icon: 'spark',  label: 'Bulk Campaigns' },
    { to: '/approvals', icon: 'check',  label: 'Approval Queue' },
    { to: '/audit',     icon: 'clock',  label: 'Audit Log' },
    { to: '/rules',     icon: 'shield', label: 'Rules Config' },
  ]},
  { section: 'Creative', items: [
    // `end` on the library link, or every child route would light it up too.
    { to: '/creative', end: true,   icon: 'image',  label: 'Library' },
    { to: '/creative/studio',       icon: 'layers', label: 'Agentic Studio' },
    { to: '/creative/feed-studio',  icon: 'cards',  label: 'Feed Studio (new)' },
    { to: '/creative/brand',        icon: 'frame',  label: 'Brand Guidelines' },
  ]},
  { section: 'AI Reporting', items: [
    { to: '/reporting', end: true,  icon: 'target', label: 'Dashboard' },
    { to: '/reporting/optibid',     icon: 'up',     label: 'Opti-Bid' },
    { to: '/reporting/insights',    icon: 'info',   label: 'AI Insights' },
  ]},
  { section: null, items: [
    { to: '/settings', icon: 'user', label: 'Settings' },
  ]},
];

// Each item's position in the flat list, for the label cascade: labels fade
// in 15ms apart as the rail unfurls, reading top to bottom.
let __flat = 0;
const NAV_INDEXED = NAV.map((g) => ({
  ...g,
  items: g.items.map((it) => ({ ...it, i: __flat++ })),
}));

/**
 * Client picker, living inside the rail.
 *
 * Hidden entirely when the user has no clients; a single client renders as a
 * plain label rather than a dropdown — a menu with one option is furniture.
 * Collapsed, it shows the client's initials in a brass ring, so which account
 * you are working in is visible even at 68px.
 */
function ClientSwitcher() {
  const { clients, agencyName, selectedId, selected, selectClient, loading } = useClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (loading || clients.length === 0) return null;

  const label = selectedId === 'ALL'
    ? 'All clients'
    : (selected ? selected.name : 'Select a client');

  const initials = selectedId === 'ALL'
    ? '∗'
    : (selected?.name || '?')
        .split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const single = clients.length === 1;

  return (
    <div className="rl-switch" ref={ref}>
      <button
        type="button"
        className={`rl-switch-btn${open ? ' open' : ''}`}
        onClick={() => !single && setOpen((o) => !o)}
        disabled={single}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
      >
        <span className="rl-chip">{initials}</span>
        <span className="rl-switch-text">
          {agencyName && <span className="rl-agency">{agencyName}</span>}
          <span className="rl-client">{label}</span>
        </span>
        {!single && <Icon name="down" size={13} strokeWidth={2.4} className="rl-switch-caret" />}
      </button>

      {open && (
        <div className="rl-menu" role="listbox">
          <button
            type="button"
            role="option"
            aria-selected={selectedId === 'ALL'}
            className={`rl-opt${selectedId === 'ALL' ? ' sel' : ''}`}
            onClick={() => { selectClient('ALL'); setOpen(false); }}
          >
            <span className="rl-opt-name">All clients</span>
            {selectedId === 'ALL' && <Icon name="check" size={14} strokeWidth={2.6} />}
          </button>
          {clients.map((c) => (
            <button
              type="button"
              key={c.client_id}
              role="option"
              aria-selected={selectedId === c.client_id}
              className={`rl-opt${selectedId === c.client_id ? ' sel' : ''}`}
              onClick={() => { selectClient(c.client_id); setOpen(false); }}
            >
              <span className="rl-opt-name">
                {c.name}
                {c.primary_account_id && (
                  <span className="rl-opt-sub">{c.primary_account_id}</span>
                )}
              </span>
              {selectedId === c.client_id && <Icon name="check" size={14} strokeWidth={2.6} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar({ user }) {
  const location = useLocation();
  const navRef = useRef(null);
  const { observeMode } = useClient();

  // Clicking a destination puts the rail to rest: it collapses immediately,
  // pointer still inside, and stays collapsed until the pointer leaves and
  // comes back. Navigating is finished — the rail has no reason to stay open.
  const [resting, setResting] = useState(false);

  // The traveller: one pill that glides to wherever the active route is.
  // Measured from the DOM on every route change, because section labels make
  // item positions unequal and arithmetic would drift.
  const [glideTop, setGlideTop] = useState(null);

  useEffect(() => {
    const el = navRef.current?.querySelector('.rl-item.active');
    setGlideTop(el ? el.offsetTop : null);
  }, [location.pathname]);

  // Resting also drops focus. The clicked link would otherwise hold
  // :focus-within, and the rail would spring back open the moment the
  // pointer left — the exact opposite of what the click just asked for.
  const rest = useCallback(() => {
    setResting(true);
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }, []);
  const wake = useCallback(() => setResting(false), []);

  return (
    <aside
      className={`rail${resting ? ' resting' : ''}`}
      onMouseLeave={wake}
    >
      <div className="rl-logo">
        <NavLink to="/" className="logo-mark" onClick={rest}>
          <div className="logo-chevrons">
            <div className="logo-chevron"></div>
            <div className="logo-chevron"></div>
            <div className="logo-dot"></div>
          </div>
          <div className="logo-text rl-fade">bid<span>sense</span></div>
        </NavLink>
      </div>

      <ClientSwitcher />

      <nav className="rl-nav" ref={navRef}>
        {glideTop != null && (
          <div className="rl-glide" style={{ top: glideTop }} aria-hidden="true" />
        )}
        {NAV_INDEXED.map((group, gi) => (
          <React.Fragment key={gi}>
            {group.section
              ? <div className="rl-sect rl-fade">{group.section}</div>
              : gi > 0 && <div className="rl-gap" />}
            {group.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) => `rl-item${isActive ? ' active' : ''}`}
                style={{ '--i': it.i }}
                onClick={rest}
                title={it.label}
              >
                <Icon name={it.icon} size={17} strokeWidth={1.9} />
                <span className="rl-fade">{it.label}</span>
              </NavLink>
            ))}
          </React.Fragment>
        ))}
      </nav>

      <div className="rl-foot">
        {/* This said "Observe Mode — nothing is blocked" unconditionally while
            the engine was enforcing, so the product told every user the
            opposite of what it was doing. It now reports what the engine
            actually reports. */}
        <div
          className={observeMode ? 'rl-observe' : 'rl-observe rl-enforcing'}
          title={observeMode
            ? 'Observe mode — decisions are recorded, nothing is blocked'
            : 'Enforcing — blocked actions do not reach the platform'}
        >
          <Icon name={observeMode ? 'eye' : 'shield'} size={14} strokeWidth={2} />
          <span className="rl-fade">{observeMode ? 'Observe mode' : 'Enforcing'}</span>
        </div>
        {user && (
          <div className="rl-user">
            {user.photoURL
              ? <img src={user.photoURL} alt="" className="rl-avatar" />
              : <span className="rl-avatar rl-avatar-ph"><Icon name="user" size={14} strokeWidth={2} /></span>}
            <div className="rl-user-text rl-fade">
              <div className="rl-user-name">{user.displayName || user.email}</div>
              <button type="button" className="rl-signout" onClick={() => signOut(auth)}>
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * A verified sign-in with no client grants is authenticated but entitled to
 * nothing. Showing an empty dashboard would leave the person guessing; saying
 * so plainly is both kinder and easier to support.
 */
function NoAccess({ error }) {
  return (
    <div className="gate">
      <div className="card" style={{ maxWidth: 460, padding: '28px 32px' }}>
        <h2 style={{ fontSize: 18, color: 'var(--pine)', marginBottom: 10 }}>No client access</h2>
        <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.55, marginBottom: 16 }}>
          {error || 'Your account is not yet linked to any clients in BidSense. Ask an administrator at your agency to grant you access.'}
        </p>
        <button className="btn btn-secondary" onClick={() => signOut(auth)}>Sign out</button>
      </div>
    </div>
  );
}

function Shell({ user }) {
  const { loading, error, clients } = useClient();

  if (loading) {
    return <div className="gate gate-pine">Loading…</div>;
  }

  if (error || clients.length === 0) {
    return <NoAccess error={error} />;
  }

  return (
    <BrowserRouter>
      <div className="app-layout">
        <Sidebar user={user} />
        <main className="main-content rail-offset">
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/build" element={<CampaignBuild />} />
            <Route path="/bulk" element={<BulkCampaigns />} />
            <Route path="/approvals" element={<ApprovalQueue />} />
            <Route path="/audit" element={<AuditLog />} />
            <Route path="/rules" element={<RulesConfig />} />
            <Route path="/creative" element={<CreativeLibrary />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/creative/studio" element={<AgenticStudio />} />
            <Route path="/creative/feed-studio" element={<FeedStudio />} />
            <Route path="/creative/brand" element={<BrandGuidelines />} />
            <Route path="/reporting" element={<ReportingDashboard />} />
            <Route path="/reporting/optibid" element={<OptiBid />} />
            <Route path="/reporting/insights" element={<AIInsights />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div className="gate gate-pine">Loading…</div>;
  }

  if (!user) {
    return <Login />;
  }

  return (
    <ClientProvider>
      <Shell user={user} />
    </ClientProvider>
  );
}

export default App;